import { Navigate } from 'react-router-dom';
import { useIsDesktop } from '../../hooks/useIsDesktop.js';
import { ConnectionError } from '../../components/loading/ConnectionError.js';
import { isDiagnosed } from '../../lib/englishApi.js';
import { useEnglishLevel } from '../leveling/useEnglishLevel.js';
import { HomeMobile } from './HomeMobile.js';
import { HomeDesktop } from './HomeDesktop.js';

/**
 * Home responsive. Ambas variantes consumen los MISMOS datos (hooks de
 * `parts/`) y primitivos compartidos (`parts/ProgressRing`, `parts/LessonRow`,
 * doodles); solo difiere la composición del layout (columna + tab bar vs
 * sidebar + multi-columna). El breakpoint vive en `useIsDesktop`.
 *
 * Además gatea el onboarding de nivel: un user autenticado que todavía no pasó
 * el triage (`onboarded === false`) es redirigido a `/onboarding/level`. El
 * estado de nivel se lee una sola vez acá y se pasa a las variantes (chip del
 * header + nivel real del CTA), evitando un segundo fetch.
 */
export function HomeScreen() {
  const isDesktop = useIsDesktop();
  const { state, error, loading, refresh, startExplore, stopExplore, selfSet } = useEnglishLevel();

  // Carga fría fallida (sin caché de sesión): `/state` no respondió y no hay
  // dato previo para degradar. Sin esto el gate `level !== null` de las
  // variantes nunca se cumple → AppLoader infinito. Con caché, `state` no es
  // null y el Home se muestra con el dato stale mientras revalida (no entra acá).
  if (!state && error && !loading) {
    return <ConnectionError onRetry={() => void refresh()} />;
  }

  // Gate del onboarding. Chequea el estado por su type-guard (`onboarded` +
  // `cefrGlobal != null`, las dos caras de "ya lo diagnosticamos") en vez de
  // sólo el flag: así el Home recibe un estado con nivel GARANTIZADO y sus
  // variantes no necesitan inventar uno para renderizar el anillo.
  if (state && !isDiagnosed(state)) {
    return <Navigate to="/onboarding/level" replace />;
  }

  // El gate del motor A0 (plan 32) vive en `HomeOrA0`, el dispatcher de `/home`.
  // Con la app A0 dormida (plan 48) el principiante absoluto también llega acá:
  // este Home es el de todos los niveles.
  return isDesktop ? (
    <HomeDesktop
      level={state}
      onStartExplore={startExplore}
      onStopExplore={stopExplore}
      onSelfSet={selfSet}
    />
  ) : (
    <HomeMobile
      level={state}
      onStartExplore={startExplore}
      onStopExplore={stopExplore}
      onSelfSet={selfSet}
    />
  );
}
